import React, { useRef, useCallback } from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import { WrappedComponentProps, injectIntl } from 'react-intl';

import { Bar, BarText, BarIcon } from '../../../../panda';

import { InputTitle } from '../../../Input';
import DeleteBtn from '../Base/DeleteBtn';
import SpaceConfigs from '../Base/SpaceConfigs';
import BackgroundConfigs from '../Base/BackgroundConfigs';
import DuplicateBtn from '../Base/DuplicateBtn';

import { IDispatch, IRootState } from '../../../../modules/stores';
import { ISpace } from '../../../../types/space';
import { ElementTypes } from '../../../../types/element';
import { GLYPHS } from '../../../../constants/icons';
import { uploadFiles, showUploadWarnToast } from '../../../../lib/pandaPicker';

type IConnectProps = ReturnType<typeof mapDispatch> & ReturnType<typeof mapState>;
interface IProps extends IConnectProps, WrappedComponentProps {
  space: ISpace<ElementTypes.StaticText>;
}

const EditFormStaticAttachment: React.FC<IProps> = ({
  workspaceId,
  blockId,
  space,
  setToast,
  updateSpaceConfig,
  intl: { formatMessage },
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const onUploadClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      uploadFiles({
        pathOptions: {
          workspaceId,
          blockId,
        },
        files: [file],
        maxFileCount: 1,

        onSuccess: (data) => {
          if (data[0]) {
            updateSpaceConfig({
              id: space.id,
              updates: {
                imageContent: {
                  url: data[0].url,
                  largeThumbUrl: data[0].largeThumbUrl,
                  name: data[0].fileName,
                },
              },
            });
          }
        },
        onError: () => {},
        onWarn: showUploadWarnToast(setToast),
      });
    },
    [workspaceId, blockId, setToast, space.id, updateSpaceConfig],
  );

  return (
    <>
      <input
        accept="image/*"
        type="file"
        id="avatar-upload"
        ref={inputRef}
        style={{ display: 'none' }}
        hidden={true}
        onChange={onUploadClick}
      />
      <SpaceConfigs space={space} />
      <InputTitle
        isSmall
        isReverse
        title={formatMessage({ id: 'sidebar.upload' })}
        css="display: block"
      />
      <Bar isSmall includeSpaceBelow isReverse>
        <BarIcon
          isSmall
          isReverse
          icon={GLYPHS.UPLOAD}
          onClick={() => {
            if (inputRef.current) inputRef.current.click();
          }}
          iconSize={{ height: 16 }}
        />
        <BarText isReverse isFullWidth text={get(space, 'config.imageContent.name')} />
      </Bar>
      <BackgroundConfigs space={space} />
      <DuplicateBtn spaceId={space.id} />
      <DeleteBtn />
    </>
  );
};

const mapState = ({ core }: IRootState) => ({
  workspaceId: core.currentWorkspaceId,
  blockId: core.currentBlockId,
});
const mapDispatch = ({ core, space }: IDispatch) => ({
  setToast: (payload: any) => core.setToast(payload),
  updateSpaceConfig: (payload: any) => space.updateSpaceConfig(payload),
});

export default compose(
  injectIntl,
  connect(
    mapState,
    // @ts-ignore
    mapDispatch,
  ),
)(EditFormStaticAttachment);
